import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Asaid from '../../components/layout/asaid.component';
import Header from '../../components/layout/header.component';
import Footer from '../../components/layout/footer.component';
import Icon from '../../components/forms/icon.component';
import useBgPages from '../../hooks/use-bg-pages.hook';
import { MANUAL_SECTIONS } from './content';

/**
 * Chrome del manual: el layout de la app + un menú lateral propio con las
 * secciones. Los estilos van inline en un <style> porque `src/index.scss` no
 * está importado en `index.tsx` (los estilos propios del proyecto hoy no
 * aplican) y no quiero que el manual dependa de que eso se arregle.
 */
export default function ManualShell({ children }: { children: React.ReactNode }) {
  useBgPages();
  const { pathname } = useLocation();

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <style>{`
        .manual-code {
          background: var(--bs-gray-200);
          color: var(--bs-gray-900);
          padding: .1rem .35rem;
          border-radius: .35rem;
          font-size: .875em;
          white-space: nowrap;
        }
        .manual-pre {
          background: var(--bs-gray-900);
          color: var(--bs-gray-100);
          overflow-x: auto;
          font-size: .8125rem;
          line-height: 1.7;
        }
        .manual-pre--formula {
          background: var(--bs-gray-800);
          font-size: .875rem;
        }
        .manual-toc a { text-decoration: none; }
        .manual-toc a:hover { text-decoration: underline; }
        .manual-anchor {
          opacity: 0;
          transition: opacity .15s ease;
          text-decoration: none;
          margin-left: .5rem;
          font-weight: 400;
        }
        .manual-topic:hover .manual-anchor { opacity: .5; }
        .manual-topic { scroll-margin-top: 100px; }
      `}</style>

      <Asaid />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <div className="row g-6">
                {/* Menú de secciones */}
                <div className="col-lg-3 col-xxl-2">
                  <div
                    className="card shadow-sm border-0 rounded-4 sticky-lg-top"
                    style={{ top: 100 }}
                  >
                    <div className="card-body p-4">
                      <Link
                        to="/manual"
                        className={`d-flex align-items-center rounded-3 px-3 py-2 mb-2 text-decoration-none ${
                          pathname === '/manual'
                            ? 'bg-light-primary text-primary fw-bold'
                            : 'text-gray-700'
                        }`}
                      >
                        <Icon icon="ki-home-2" size="fs-4" className="me-2" />
                        <span className="fs-7">Portada</span>
                      </Link>
                      <div className="separator my-2" />
                      {MANUAL_SECTIONS.map((s) => {
                        const active = pathname === `/manual/${s.slug}`;
                        return (
                          <Link
                            key={s.slug}
                            to={`/manual/${s.slug}`}
                            className={`d-flex align-items-center rounded-3 px-3 py-2 mb-1 text-decoration-none ${
                              active ? 'bg-light-primary text-primary fw-bold' : 'text-gray-700'
                            }`}
                          >
                            <Icon icon={s.icon} size="fs-4" className="me-2 flex-shrink-0" />
                            <span className="fs-7">{s.title}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Contenido */}
                <div className="col-lg-9 col-xxl-10">{children}</div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
